import { useEffect, useState } from 'react';
import {
  getAdminComplianceForms, saveComplianceForm, assignComplianceForm,
  generateComplianceQuiz,
  GetAdminComplianceFormsOutputType,
} from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  Plus, Pencil, AlertCircle, Users, Loader2, Sparkles, CheckCircle2,
  BookOpen, ClipboardList, HelpCircle, RefreshCw, Award, Eye,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import ComplianceResourceEditor, { ResourceLink, ResourceFile } from './ComplianceResourceEditor';
import ComplianceSubmissionModal from './ComplianceSubmissionModal';

type ComplianceForm = GetAdminComplianceFormsOutputType['forms'][0];
type Submission = GetAdminComplianceFormsOutputType['allSubmissions'][0];

const FORM_TYPES = ['Allergen Awareness', 'Food Hygiene Level 2', 'DPS Authorisation', 'Health & Safety', 'Fire Awareness', 'Working Time Directive', 'Custom'];

const fmtDate = (d?: string) => { try { return d ? format(parseISO(d), 'dd MMM yyyy') : '—'; } catch { return d || '—'; } };

function parseLinks(raw?: string): ResourceLink[] {
  if (!raw) return [];
  try {
    const p = JSON.parse(raw);
    if (Array.isArray(p)) return p as ResourceLink[];
  } catch { /* fall through */ }
  return raw.split('\n').map(l => l.trim()).filter(Boolean).map(l => ({ label: l, url: l.startsWith('http') ? l : '' }));
}

const blankForm = {
  formName: '', formType: '', formBody: '', declarationItems: '',
  consequenceText: '', passMark: 80, renewalMonths: 12,
  dpsHolderName: '', dpsLicenceNumber: '', premisesLicenceNumber: '', status: 'Draft',
  certificateEnabled: false,
};

const STATUS_COLORS: Record<string, string> = {
  'Signed': 'bg-emerald-500/15 text-emerald-600',
  'Quiz Passed': 'bg-primary/10 text-primary',
  'Declaration': 'bg-yellow-500/10 text-yellow-600',
  'Study': 'bg-blue-500/10 text-blue-600',
  'Not Started': 'bg-muted text-muted-foreground',
  'Overdue': 'bg-destructive/10 text-destructive',
};

export default function ComplianceAdminTab() {
  const [data, setData] = useState<GetAdminComplianceFormsOutputType | null>(null);
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...blankForm });
  const [resourceLinks, setResourceLinks] = useState<ResourceLink[]>([]);
  const [resourceFiles, setResourceFiles] = useState<ResourceFile[]>([]);
  const [saving, setSaving] = useState(false);
  const [formTab, setFormTab] = useState('content');

  const [generatingQuiz, setGeneratingQuiz] = useState(false);
  const [generatedQuestions, setGeneratedQuestions] = useState<{ id: string; question: string }[]>([]);

  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignFormId, setAssignFormId] = useState('');
  const [assignFormName, setAssignFormName] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [assignDeadline, setAssignDeadline] = useState('');
  const [consequenceText, setConsequenceText] = useState('');
  const [assigning, setAssigning] = useState(false);

  const [subTab, setSubTab] = useState('forms');
  const [subSearch, setSubSearch] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
  const [submissionModalOpen, setSubmissionModalOpen] = useState(false);

  const load = () => {
    setLoading(true);
    getAdminComplianceForms({}).then(setData).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditId(null);
    setForm({ ...blankForm });
    setResourceLinks([]);
    setResourceFiles([]);
    setGeneratedQuestions([]);
    setFormTab('content');
    setSheetOpen(true);
  };

  const openEdit = (f: ComplianceForm) => {
    setEditId(f.id);
    setForm({
      formName: f.formName, formType: f.formType || '', formBody: f.formBody || '',
      declarationItems: f.declarationItems || '',
      consequenceText: f.consequenceText || '', passMark: f.passMark || 80,
      renewalMonths: f.renewalMonths || 12, dpsHolderName: f.dpsHolderName || '',
      dpsLicenceNumber: f.dpsLicenceNumber || '', premisesLicenceNumber: f.premisesLicenceNumber || '',
      status: f.status,
      certificateEnabled: f.certificateEnabled || false,
    });
    setResourceLinks(parseLinks(f.studyLinks));
    setResourceFiles((f.resourceFiles || []).map(rf => {
      const urlParts = rf.url.split('/');
      const name = decodeURIComponent(urlParts[urlParts.length - 1]?.split('?')[0] || 'Document');
      return { url: rf.url, name };
    }));
    setGeneratedQuestions([]);
    setFormTab('content');
    setSheetOpen(true);
  };

  const openAssign = (f: ComplianceForm) => {
    setAssignFormId(f.id);
    setAssignFormName(f.formName);
    setConsequenceText(f.consequenceText || '');
    setSelectedEmployees([]);
    setAssignDeadline('');
    setAssignDialogOpen(true);
  };

  const openSubmission = (s: Submission) => {
    setSelectedSubmission(s);
    setSubmissionModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.formName.trim()) { toast.error('Form name is required'); return; }
    setSaving(true);
    try {
      await saveComplianceForm({
        id: editId || undefined,
        formName: form.formName,
        formType: form.formType || undefined,
        formBody: form.formBody || undefined,
        declarationItems: form.declarationItems || undefined,
        studyLinks: resourceLinks.length > 0 ? JSON.stringify(resourceLinks) : undefined,
        consequenceText: form.consequenceText || undefined,
        passMark: form.passMark,
        renewalMonths: form.renewalMonths,
        dpsHolderName: form.dpsHolderName || undefined,
        dpsLicenceNumber: form.dpsLicenceNumber || undefined,
        premisesLicenceNumber: form.premisesLicenceNumber || undefined,
        status: form.status,
        certificateEnabled: form.certificateEnabled,
        resourceFileUrls: resourceFiles.length > 0 ? resourceFiles : undefined,
      });
      toast.success(editId ? 'Form updated' : 'Form created');
      setSheetOpen(false);
      load();
    } catch (e: any) { toast.error(e.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleGenerateQuiz = async () => {
    if (!editId) { toast.error('Save the form first before generating quiz questions'); return; }
    setGeneratingQuiz(true);
    try {
      const res = await generateComplianceQuiz({ formId: editId, replaceExisting: true });
      setGeneratedQuestions(res.questions.map(q => ({ id: q.id, question: q.question })));
      toast.success(`${res.questionsGenerated} quiz questions generated`);
      load();
    } catch (e: any) { toast.error(e.message || 'Failed to generate quiz'); }
    finally { setGeneratingQuiz(false); }
  };

  const handleAssign = async () => {
    if (selectedEmployees.length === 0) { toast.error('Select at least one employee'); return; }
    setAssigning(true);
    try {
      const res = await assignComplianceForm({
        formId: assignFormId,
        formName: assignFormName,
        employeeIds: selectedEmployees,
        deadline: assignDeadline || undefined,
        consequenceText: consequenceText || undefined,
      });
      toast.success(`Assigned to ${res.assigned} employee(s)`);
      setAssignDialogOpen(false);
      load();
    } catch (e: any) { toast.error(e.message || 'Failed'); }
    finally { setAssigning(false); }
  };

  const toggleEmployee = (id: string) => {
    setSelectedEmployees(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const filteredSubmissions = (data?.allSubmissions || []).filter(s => {
    if (!subSearch) return true;
    const q = subSearch.toLowerCase();
    return s.employeeName.toLowerCase().includes(q) || s.formName.toLowerCase().includes(q) || s.status.toLowerCase().includes(q);
  });

  if (loading) return <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12" />)}</div>;

  return (
    <div>
      <Tabs value={subTab} onValueChange={setSubTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="forms">Forms</TabsTrigger>
          <TabsTrigger value="submissions">Submissions</TabsTrigger>
        </TabsList>

        {/* ─── FORMS TAB ─── */}
        <TabsContent value="forms">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-muted-foreground">{data?.forms.length || 0} compliance forms</p>
            <Button onClick={openNew} className="bg-primary text-primary-foreground" size="sm">
              <Plus className="w-4 h-4 mr-1" /> New Form
            </Button>
          </div>

          <div className="border border-border overflow-hidden rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">Form</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground hidden md:table-cell">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground hidden sm:table-cell">Resources</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground hidden sm:table-cell">Completions</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground hidden lg:table-cell">Cert</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data?.forms.map(f => {
                  const linkCount = parseLinks(f.studyLinks).length;
                  const fileCount = (f.resourceFiles || []).length;
                  return (
                    <tr key={f.id}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-foreground">{f.formName}</p>
                        {f.submissionsOverdue > 0 && (
                          <p className="text-xs text-destructive flex items-center gap-1 mt-0.5">
                            <AlertCircle className="w-3 h-3" /> {f.submissionsOverdue} overdue
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground hidden md:table-cell">{f.formType || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-sm ${f.status === 'Active' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                          {f.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {f.quizQuestionsCount > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-xs text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                              <HelpCircle className="w-2.5 h-2.5" /> {f.quizQuestionsCount}Q
                            </span>
                          )}
                          {linkCount > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                              <BookOpen className="w-2.5 h-2.5" /> {linkCount}
                            </span>
                          )}
                          {fileCount > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                              <ClipboardList className="w-2.5 h-2.5" /> {fileCount}
                            </span>
                          )}
                          {f.quizQuestionsCount === 0 && linkCount === 0 && fileCount === 0 && (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground hidden sm:table-cell">
                        <span className="text-primary font-medium">{f.submissionsSigned}</span> / {f.submissionsTotal}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        {f.certificateEnabled
                          ? <span className="inline-flex items-center gap-1 text-xs text-primary bg-primary/10 px-1.5 py-0.5 rounded"><Award className="w-2.5 h-2.5" /> On</span>
                          : <span className="text-xs text-muted-foreground">Off</span>
                        }
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => openEdit(f)}>
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => openAssign(f)}>
                            <Users className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ─── SUBMISSIONS TAB ─── */}
        <TabsContent value="submissions">
          <div className="mb-3">
            <Input
              placeholder="Search by employee, form or status…"
              value={subSearch}
              onChange={e => setSubSearch(e.target.value)}
              className="h-9 max-w-sm"
            />
          </div>
          <div className="border border-border overflow-hidden rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">Employee</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">Form</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground hidden md:table-cell">Deadline</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground hidden lg:table-cell">Score</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground hidden lg:table-cell">Cert</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredSubmissions.map(s => (
                  <tr key={s.id} className="hover:bg-muted/30 cursor-pointer transition-colors" onClick={() => openSubmission(s)}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-foreground text-sm">{s.employeeName}</p>
                      {s.employeeEmail && <p className="text-xs text-muted-foreground">{s.employeeEmail}</p>}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{s.formName}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-sm ${STATUS_COLORS[s.status] || STATUS_COLORS['Not Started']}`}>
                        {s.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground hidden md:table-cell">{fmtDate(s.deadline)}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground hidden lg:table-cell">
                      {s.quizScore != null ? `${s.quizScore}%` : '—'}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      {s.status === 'Signed' && s.certificateEnabled
                        ? <span className="inline-flex items-center gap-1 text-xs text-primary"><Award className="w-3 h-3" /> Ready</span>
                        : <span className="text-xs text-muted-foreground">—</span>
                      }
                    </td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <Button
                        size="sm" variant="ghost" className="h-7 px-2 text-xs"
                        onClick={() => openSubmission(s)}
                      >
                        <Eye className="w-3 h-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {filteredSubmissions.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">No submissions found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* ─── Edit/Create Form Sheet ─── */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle>{editId ? 'Edit Form' : 'New Compliance Form'}</SheetTitle>
          </SheetHeader>

          <Tabs value={formTab} onValueChange={setFormTab}>
            <TabsList className="mb-4 w-full">
              <TabsTrigger value="content" className="flex-1">Content</TabsTrigger>
              <TabsTrigger value="resources" className="flex-1">Resources</TabsTrigger>
              <TabsTrigger value="quiz" className="flex-1">Quiz</TabsTrigger>
              <TabsTrigger value="declaration" className="flex-1">Declaration</TabsTrigger>
              <TabsTrigger value="certificate" className="flex-1">Certificate</TabsTrigger>
            </TabsList>

            {/* CONTENT TAB */}
            <TabsContent value="content" className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Form Name *</label>
                  <Input value={form.formName} onChange={e => setForm(f => ({ ...f, formName: e.target.value }))} placeholder="e.g. Allergen Awareness" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Form Type</label>
                  <Select value={form.formType || 'none'} onValueChange={v => setForm(f => ({ ...f, formType: v === 'none' ? '' : v }))}>
                    <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not set</SelectItem>
                      {FORM_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Pass Mark %</label>
                  <Input type="number" value={form.passMark} onChange={e => setForm(f => ({ ...f, passMark: parseInt(e.target.value) || 80 }))} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Renewal (months)</label>
                  <Input type="number" value={form.renewalMonths} onChange={e => setForm(f => ({ ...f, renewalMonths: parseInt(e.target.value) || 0 }))} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Status</label>
                  <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Draft">Draft</SelectItem>
                      <SelectItem value="Active">Active</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Study Content Body</label>
                <Textarea
                  value={form.formBody}
                  onChange={e => setForm(f => ({ ...f, formBody: e.target.value }))}
                  className="min-h-32"
                  placeholder="Paste or type the full compliance content here. This text will be shown to employees as study material and used by AI to generate quiz questions."
                />
              </div>

              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Consequence Text</label>
                <Input value={form.consequenceText} onChange={e => setForm(f => ({ ...f, consequenceText: e.target.value }))} placeholder="Failure to complete means you cannot work" />
              </div>

              {form.formType === 'DPS Authorisation' && (
                <div className="border border-border rounded p-3 space-y-3">
                  <p className="text-xs font-medium text-foreground">DPS Details</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">DPS Holder Name</label>
                      <Input value={form.dpsHolderName} onChange={e => setForm(f => ({ ...f, dpsHolderName: e.target.value }))} />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Personal Licence No.</label>
                      <Input value={form.dpsLicenceNumber} onChange={e => setForm(f => ({ ...f, dpsLicenceNumber: e.target.value }))} />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Premises Licence No.</label>
                      <Input value={form.premisesLicenceNumber} onChange={e => setForm(f => ({ ...f, premisesLicenceNumber: e.target.value }))} />
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* RESOURCES TAB */}
            <TabsContent value="resources">
              <ComplianceResourceEditor
                links={resourceLinks}
                files={resourceFiles}
                onLinksChange={setResourceLinks}
                onFilesChange={setResourceFiles}
              />
            </TabsContent>

            {/* QUIZ TAB */}
            <TabsContent value="quiz" className="space-y-4">
              <div className="bg-muted/40 border border-border rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground">AI Quiz Generator</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Generates quiz questions from the study content body and any web links you've added. Save the form first, then generate.
                    </p>
                    {!editId && (
                      <p className="text-xs text-amber-500 mt-2 font-medium">⚠ Save the form first to enable quiz generation</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button onClick={handleGenerateQuiz} disabled={generatingQuiz || !editId} className="bg-primary text-primary-foreground" size="sm">
                    {generatingQuiz ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Generating...</> : <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Generate Quiz Questions</>}
                  </Button>
                  {generatedQuestions.length > 0 && (
                    <Button onClick={handleGenerateQuiz} disabled={generatingQuiz || !editId} variant="outline" size="sm">
                      <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Regenerate
                    </Button>
                  )}
                </div>
              </div>
              {generatedQuestions.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                    <p className="text-sm font-semibold text-foreground">{generatedQuestions.length} Questions Generated</p>
                  </div>
                  <div className="space-y-2">
                    {generatedQuestions.map((q, i) => (
                      <div key={q.id} className="flex items-start gap-2.5 bg-muted/30 rounded p-2.5 text-xs">
                        <span className="text-muted-foreground font-mono shrink-0">Q{i + 1}</span>
                        <p className="text-foreground">{q.question}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {generatedQuestions.length === 0 && editId && (
                <div className="text-center py-6 text-muted-foreground">
                  <HelpCircle className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No quiz questions yet</p>
                  <p className="text-xs mt-1">Click Generate to create questions from your content</p>
                </div>
              )}
            </TabsContent>

            {/* DECLARATION TAB */}
            <TabsContent value="declaration" className="space-y-4">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Declaration Items (one per line)</label>
                <Textarea
                  value={form.declarationItems}
                  onChange={e => setForm(f => ({ ...f, declarationItems: e.target.value }))}
                  className="min-h-32"
                  placeholder={"I understand the 14 major allergens\nI will always use the Allergen Check modifier\nI know to escalate any allergen queries to the manager"}
                />
                <p className="text-xs text-muted-foreground mt-1">Each line becomes a checkbox that employees must tick before signing.</p>
              </div>
            </TabsContent>

            {/* CERTIFICATE TAB */}
            <TabsContent value="certificate" className="space-y-4">
              <div className="bg-muted/30 border border-border rounded-lg p-4 flex items-start gap-3">
                <Award className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-foreground">Certificate of Completion</p>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    When enabled, a printable A4 landscape Rattlebone certificate becomes available for all completed submissions under this form. Managers can view any signed submission and print a professional hard copy to present to the employee in person.
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 border border-border rounded-lg bg-card">
                <div>
                  <p className="text-sm font-semibold text-foreground">Enable Certificate for This Form</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {form.certificateEnabled
                      ? 'Certificate is ON — managers can print hard copies for signed submissions'
                      : 'Certificate is OFF — no certificates will be available for submissions'}
                  </p>
                </div>
                <Switch
                  checked={form.certificateEnabled}
                  onCheckedChange={v => setForm(f => ({ ...f, certificateEnabled: v }))}
                />
              </div>

              {form.certificateEnabled && (
                <div className="border border-primary/20 bg-primary/5 rounded-lg p-4 space-y-2">
                  <p className="text-xs font-semibold text-primary uppercase tracking-wider">Certificate Details</p>
                  <ul className="space-y-1.5 text-xs text-foreground">
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" /> Employee's name, role and department</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" /> Full list of training topics covered</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" /> Assessment score and pass mark</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" /> Date of completion and certificate reference</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" /> Official Rattlebone branding and regulatory guidance</li>
                  </ul>
                  <p className="text-xs text-muted-foreground pt-1">
                    To print: go to <strong>Submissions</strong>, click on a signed submission, and use the <strong>Print Certificate</strong> button.
                  </p>
                </div>
              )}
            </TabsContent>
          </Tabs>

          <div className="mt-6 pt-4 border-t border-border">
            <Button onClick={handleSave} disabled={saving} className="w-full bg-primary text-primary-foreground">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : editId ? 'Update Form' : 'Create Form'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* ─── Assign Dialog ─── */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign: {assignFormName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Deadline</label>
              <Input type="date" value={assignDeadline} onChange={e => setAssignDeadline(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Consequence Text</label>
              <Input value={consequenceText} onChange={e => setConsequenceText(e.target.value)} placeholder="What happens if not completed" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">
                Select Employees ({selectedEmployees.length} selected)
              </label>
              <div className="max-h-48 overflow-y-auto border border-border rounded divide-y divide-border">
                {data?.employees.map(emp => (
                  <button
                    key={emp.id}
                    onClick={() => toggleEmployee(emp.id)}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors ${selectedEmployees.includes(emp.id) ? 'bg-primary/10 text-primary' : 'hover:bg-muted text-foreground'}`}
                  >
                    {emp.name}
                  </button>
                ))}
              </div>
            </div>
            <Button onClick={handleAssign} disabled={assigning || selectedEmployees.length === 0} className="w-full bg-primary text-primary-foreground">
              {assigning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Assigning...</> : `Assign to ${selectedEmployees.length} employee(s)`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Submission Detail Modal ─── */}
      <ComplianceSubmissionModal
        submission={selectedSubmission}
        open={submissionModalOpen}
        onClose={() => { setSubmissionModalOpen(false); setSelectedSubmission(null); }}
      />
    </div>
  );
}
